// @flow

import * as React from "react"
import {connect} from "react-redux"
import {withTranslation} from "react-i18next"
import {set, cloneDeep} from "lodash"

import {toDinero} from "../util"
import Config from "../config"
import ConfirmModal from "../components/modals/confirm"
import Icon from "../components/icon"
import SendInvoiceModal from "../components/modals/send-invoice"
import Invoiceables from "../components/invoicing/invoiceables"
import InvoiceItemRow from "../components/invoicing/invoice/item-row"
import InvoiceStatus from "../components/invoicing/status"
import InvoiceHeader from "../components/invoicing/header"
import InvoiceFooter from "../components/invoicing/footer"
import {
  setInvoice,
  setItemsToInvoice,
  payInvoice,
  finaliseInvoice,
  regenerateInvoice,
  sendInvoice,
  updateInvoiceMeta,
  addAllItemsToInvoice,
  addItemToInvoice,
} from "../actions/invoice"
import type {
  Dispatch,
  Invoice,
  InvoiceBase,
  State as ReduxState,
  ItemToInvoice,
  InvoiceGroupedItem,
} from "../types"

type Props = {
  +i18n: any,
  +t: any,
  +invoice: ?Invoice,
  +itemsToInvoice: $ReadOnlyArray<ItemToInvoice>,
  +match: any,
  +dispatch: Dispatch,
  +finaliseError: boolean,
}

type State = {
  +isConfirmFinaliseModalOpen: boolean,
  +isConfirmPayModalOpen: boolean,
  +isConfirmRegenerateModalOpen: boolean,
  +isSendModalOpen: boolean,
  +note: string,
  +isFinal: ?boolean,
}

class InvoiceView extends React.Component<Props, State> {
  handleFinaliseClick: () => void
  handlePayClick: () => void
  handlePreviewClick: () => void
  handleConfirmFinalise: () => void
  handleCancelFinalise: () => void
  handleConfirmRegenerate: () => void
  handleCancelRegenerate: () => void
  handleConfirmPay: () => void
  handleCancelPay: () => void
  handleCloseModal: () => void
  handleAmountDiscountUpdate: (string, string) => void
  handleInvoiceHeaderUpdate: (InvoiceBase) => void
  handleNoteChange: (string) => void
  handleSend: (string) => void
  handleAddAllItemsToInvoiceClick: (
    SyntheticMouseEvent<HTMLLinkElement>
  ) => void
  handleAddInvoiceable: (ItemToInvoice) => void
  handleRegenerateClick: () => void

  constructor(props: Props) {
    super(props)

    this.state = {
      isConfirmFinaliseModalOpen: false,
      isConfirmRegenerateModalOpen: false,
      isConfirmPayModalOpen: false,
      isSendModalOpen: false,
      note: "",
      isFinal: props.invoice ? props.invoice.isFinal : undefined,
    }

    // Modals
    this.handleConfirmFinalise = this.handleConfirmFinalise.bind(this)
    this.handleCancelFinalise = this.handleCancelFinalise.bind(this)
    this.handleConfirmRegenerate = this.handleConfirmRegenerate.bind(this)
    this.handleCancelRegenerate = this.handleCancelRegenerate.bind(this)
    this.handleConfirmPay = this.handleConfirmPay.bind(this)
    this.handleCancelPay = this.handleCancelPay.bind(this)
    this.handleCloseModal = this.handleCloseModal.bind(this)

    // Other
    this.handleAmountDiscountUpdate = this.handleAmountDiscountUpdate.bind(this)
    this.handleFinaliseClick = this.handleFinaliseClick.bind(this)
    this.handlePayClick = this.handlePayClick.bind(this)
    this.handlePreviewClick = this.handlePreviewClick.bind(this)
    this.handleInvoiceHeaderUpdate = this.handleInvoiceHeaderUpdate.bind(this)
    this.handleNoteChange = this.handleNoteChange.bind(this)
    this.handleSend = this.handleSend.bind(this)
    this.handleAddAllItemsToInvoiceClick =
      this.handleAddAllItemsToInvoiceClick.bind(this)
    this.handleAddInvoiceable = this.handleAddInvoiceable.bind(this)
    this.handleRegenerateClick = this.handleRegenerateClick.bind(this)
  }

  componentDidMount() {
    const {invoice} = Config.data
    const {dispatch} = this.props
    if (invoice) {
      dispatch(setInvoice(invoice.invoice))
      dispatch(setItemsToInvoice(invoice.itemsToInvoice))
    }
    // for dashboard views, the base entities are already output in the html
  }

  static getDerivedStateFromProps({invoice}, state) {
    if (invoice && invoice.isFinal && state.isFinal == false) {
      return {
        isFinal: invoice.isFinal,
        isConfirmModalOpen: false,
        isSendModalOpen: true,
      }
    }
    if (invoice) {
      return {isFinal: invoice.isFinal}
    }
    return null
  }

  handleRegenerateClick() {
    this.setState({isConfirmRegenerateModalOpen: true})
  }

  handleAddInvoiceable(babyListItem) {
    const {dispatch, invoice} = this.props
    if (invoice) {
      dispatch(addItemToInvoice(invoice.id, babyListItem.id))
    }
  }

  handleAddAllItemsToInvoiceClick(e: SyntheticMouseEvent<HTMLLinkElement>) {
    e.preventDefault()
    const {dispatch, invoice} = this.props
    if (invoice) {
      dispatch(addAllItemsToInvoice(invoice.id))
    }
  }

  handleFinaliseClick() {
    const {invoice} = this.props
    if (!invoice) return null

    if (invoice.isFinal) {
      this.setState({
        isConfirmFinaliseModalOpen: false,
        isConfirmPayModalOpen: false,
        isSendModalOpen: true,
      })
    } else {
      this.setState({isConfirmFinaliseModalOpen: true})
    }
  }

  handlePayClick() {
    const {invoice} = this.props
    if (!invoice) return null

    if (!invoice.isPaid && invoice.isFinal) {
      this.setState({
        isConfirmFinaliseModalOpen: false,
        isConfirmPayModalOpen: true,
      })
    }
  }

  handlePreviewClick() {
    this.setState({isSendModalOpen: true})
  }

  handleConfirmRegenerate() {
    const {dispatch, invoice} = this.props
    if (invoice) {
      dispatch(regenerateInvoice(invoice.id))
      this.setState({isConfirmRegenerateModalOpen: false})
    }
  }

  handleCancelRegenerate() {
    this.setState({isConfirmRegenerateModalOpen: false})
  }

  handleConfirmFinalise() {
    const {dispatch, invoice} = this.props
    if (invoice) {
      dispatch(finaliseInvoice(invoice.id))
      this.setState({isConfirmFinaliseModalOpen: false})
    }
  }

  handleCancelFinalise() {
    this.setState({isConfirmFinaliseModalOpen: false})
  }

  handleConfirmPay() {
    const {dispatch, invoice} = this.props
    if (invoice) {
      dispatch(payInvoice(invoice.id))
      this.setState({isConfirmPayModalOpen: false})
    }
  }

  handleCancelPay() {
    this.setState({
      isConfirmPayModalOpen: false,
    })
  }

  handleCloseModal() {
    this.setState({
      isConfirmPayModalOpen: false,
      isConfirmFinaliseModalOpen: false,
      isSendModalOpen: false,
    })
  }

  handleAmountDiscountUpdate(property: string, value: string) {
    const {dispatch, invoice} = this.props
    if (invoice) {
      const invoiceUpdated = cloneDeep(invoice)
      set(invoiceUpdated, property, value)
      dispatch(updateInvoiceMeta(invoice.id, invoiceUpdated))
    }
  }

  handleInvoiceHeaderUpdate(invoice: InvoiceBase) {
    const {dispatch} = this.props
    dispatch(updateInvoiceMeta(invoice.id, invoice))
  }

  handleNoteChange(note: string) {
    const {dispatch, invoice} = this.props
    if (invoice) {
      dispatch(updateInvoiceMeta(invoice.id, {note}))
    }
  }

  handleSend(message: string) {
    const {invoice, dispatch} = this.props
    if (invoice) {
      dispatch(sendInvoice(invoice.id, message))
    }
    this.handleCloseModal()
  }

  renderModal() {
    const {
      isConfirmFinaliseModalOpen,
      isConfirmRegenerateModalOpen,
      isConfirmPayModalOpen,
      isSendModalOpen,
    } = this.state
    const {invoice, t} = this.props

    if (!invoice) return
    if (isConfirmRegenerateModalOpen) {
      return (
        <ConfirmModal
          onConfirm={this.handleConfirmRegenerate}
          onCancel={this.handleCancelRegenerate}
        />
      )
    }
    if (isConfirmFinaliseModalOpen) {
      return (
        <ConfirmModal
          onConfirm={this.handleConfirmFinalise}
          onCancel={this.handleCancelFinalise}
        />
      )
    }
    if (isConfirmPayModalOpen) {
      return (
        <ConfirmModal
          onConfirm={this.handleConfirmPay}
          onCancel={this.handleCancelPay}
        />
      )
    }
    if (isSendModalOpen) {
      const title = invoice.isFinal
        ? t(`intake.sendInvoice.title`)
        : t(`intake.sendInvoice.title_preview`)
      const text = t(`intake.sendInvoice.info`)
      return (
        <SendInvoiceModal
          title={title}
          text={text}
          email={invoice.receiver.email}
          onSend={this.handleSend}
          onCloseModal={this.handleCloseModal}
        />
      )
    }
  }

  renderSide() {
    const {t, invoice, itemsToInvoice} = this.props
    if (!invoice) return null
    const {isFinal, isPaid} = invoice
    return (
      <div className="invoice-side">
        {!isFinal && itemsToInvoice.length > 0 && (
          <div className="invoice-side-block panel">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="m-0">{t("invoice.itemsToInvoice")}</h2>
              <a
                href="#"
                className="btn btn-sm btn-outline-success"
                onClick={this.handleAddAllItemsToInvoiceClick}
              >
                {t("invoice.addAllItemsToInvoice")}
              </a>
            </div>
            <Invoiceables
              items={itemsToInvoice}
              hidePrice={true}
              onItemClick={this.handleAddInvoiceable}
            />
            <a
              href="#"
              className="btn btn-sm btn-outline-success mt-4"
              onClick={this.handleAddAllItemsToInvoiceClick}
            >
              {t("invoice.addAllItemsToInvoice")}
            </a>
          </div>
        )}

        {!isFinal && (
          <button
            className="btn btn-secondary mr-2"
            type="button"
            onClick={this.handlePreviewClick}
          >
            {t("invoice.sendDraft")}
          </button>
        )}
        <button
          className="btn btn-primary mr-2"
          type="button"
          onClick={this.handleFinaliseClick}
        >
          {isFinal ? t("invoice.finalise_send") : t("invoice.finalise")}
        </button>
        {isFinal ? (
          <button
            className="btn btn-secondary mr-2"
            type="button"
            disabled={isPaid}
            onClick={this.handlePayClick}
          >
            {isPaid ? t("invoice.paid") : t("invoice.pay")}
          </button>
        ) : null}
        <a
          href={Config.data.invoice.pdfUrl}
          className="d-inline-flex align-items-center btn"
          target="blank"
        >
          <Icon name="icon-pdf" />
          <span className="d-inline-block pl-1">{t("invoice.viewPdf")}</span>
        </a>

        <br />

        {!isFinal && (
          <button
            className="btn btn-outline-secondary btn-sm mr-2 mt-4"
            type="button"
            onClick={this.handleRegenerateClick}
          >
            {t("invoice.regenerate")}
          </button>
        )}
      </div>
    )
  }

  renderGroup(key, titleRow, rows) {
    return (
      <React.Fragment key={key}>
        <tr>
          <td colSpan="4" className="p-0">
            &nbsp;
          </td>
        </tr>
        <tr className="thead-light">{titleRow}</tr>
        {rows}
      </React.Fragment>
    )
  }

  renderRows() {
    const {t, invoice} = this.props
    if (!invoice) return null
    const {itemsGrouped, isFinal, isEditable} = invoice

    return itemsGrouped.map(({key, items}) => {
      const rows = items.map((i) => {
        if (i.id) {
          return (
            <InvoiceItemRow
              item={i}
              itemKey={key}
              isFinal={isFinal}
              isEditable={isEditable}
              key={"invoice-item-" + String(i.id)}
            />
          )
        }
        // Grouped circulars
        const group: InvoiceGroupedItem = (i: any)
        const subRows = group.items.map((i) => {
          return (
            <InvoiceItemRow
              item={i}
              itemKey={key}
              isFinal={isFinal}
              isEditable={isEditable}
              key={"invoice-item-" + String(i.id)}
            />
          )
        })
        const tagKey = "invoice-group-" + key + "-" + group.clientName
        return this.renderGroup(
          tagKey,
          <React.Fragment key={tagKey + "-th"}>
            <th colSpan="2">
              <strong>{group.clientName}</strong>
            </th>
            <th className="text-right">
              {toDinero(group.cost).toFormat("$0,0.00")}
            </th>
            <th>&nbsp;</th>
          </React.Fragment>,
          subRows
        )
      })

      let title = t("invoice.group_admin")
      if (key === "baby_list_invoice") {
        title = t("invoice.group_baby_list_invoice")
      }
      if (key === "baby_list_item") {
        title = t("invoice.group_circulars")
      }

      return this.renderGroup(
        "invoice-group-" + key,
        <th colSpan="4">
          <h3 className="mb-0">{title}</h3>
        </th>,
        rows
      )
    })
  }

  render() {
    const {t, invoice} = this.props
    if (!invoice) return null
    return (
      <div className="container invoice mt-4">
        <div className="invoice-sheet">
          <InvoiceStatus invoice={invoice} />

          <InvoiceHeader
            invoice={invoice}
            finaliseError={this.props.finaliseError}
            onUpdate={this.handleInvoiceHeaderUpdate}
          />

          <div className="invoice-sheet-content">
            <table className="table table-sm">
              <colgroup>
                <col width="" />
                <col width="80" />
                <col width="80" />
                <col width="85" />
              </colgroup>

              <thead>
                <tr>
                  <th>{t("invoice.product")}</th>
                  <th className="text-center">{t("invoice.amount")}</th>
                  <th className="text-right">{t("invoice.price")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>{this.renderRows()}</tbody>
            </table>
          </div>

          <InvoiceFooter
            isInclVAT={false}
            invoice={invoice}
            onNoteChange={this.handleNoteChange}
            onAmountDiscountUpdate={this.handleAmountDiscountUpdate}
          />
        </div>

        {this.renderSide()}
        {this.renderModal()}
      </div>
    )
  }
}

const mapStateToProps = (state: ReduxState) => ({
  invoice: state.invoice.invoice,
  finaliseError: state.invoice.finaliseError,
  itemsToInvoice: state.invoice.itemsToInvoice,
})

export default withTranslation()(connect(mapStateToProps)(InvoiceView))
